import { useQuery, useQueryClient } from "@tanstack/react-query";
import type { AxiosError } from "axios";
import { api } from "../singleton/axios";
import { useTourMode } from "../contexts/TourModeContext";
import { DashboardWatchlistItem } from "../components/Tour/mockData";

export interface DashboardWatchlistEntry {
  id: string;
  ticker: string;
  companyName: string;
  logo: string;
  marketCap: number;
  exchange: string;
  price: number;
  change: number;
  changePercentage: number;
  createdAt: string;
}

export interface DashboardWatchlistResponse {
  data: DashboardWatchlistEntry[];
  meta: {
    total: number;
  };
}

interface ApiError {
  success: false;
  error: string;
  message?: string;
  details?: string;
}

const fetchDashboardWatchlist = async (
  watchlistId?: string | null,
): Promise<DashboardWatchlistResponse> => {
  const { data } = await api.get<DashboardWatchlistResponse>("/dashboard-watchlist", {
    params: watchlistId ? { watchlistId } : undefined,
  });
  return data;
};

/**
 * Live prices for the dashboard watchlist card. Passing a watchlistId scopes
 * the response to that list; omitting it returns the union of every list the
 * user owns, which is what the tour and the zero-watchlist case rely on.
 *
 * The id is part of the query key, so each list caches separately — and every
 * existing `invalidateQueries(["dashboard-watchlist"])` still matches them all
 * by prefix.
 */
export const useDashboardWatchlist = (
  watchlistId?: string | null,
  options?: {
    /** Gate the fetch until the caller knows which list it wants — an
     *  ungated null id fires a wasted union request (every list's symbols)
     *  one render before the scoped one. Tour mode ignores the gate. */
    enabled?: boolean;
  },
) => {
  const { isTourMode, getMockData } = useTourMode();
  const queryClient = useQueryClient();

  return useQuery<DashboardWatchlistResponse, ApiError>({
    queryKey: ["dashboard-watchlist", isTourMode, watchlistId ?? null],
    enabled: isTourMode || (options?.enabled ?? true),
    queryFn: async () => {
      // Return mock data in tour mode
      if (isTourMode) {
        const mockData = getMockData<DashboardWatchlistItem[]>("dashboardWatchlist");
        if (mockData) {
          return {
            data: mockData as unknown as DashboardWatchlistEntry[],
            meta: { total: mockData.length },
          };
        }
      }

      try {
        return await fetchDashboardWatchlist(watchlistId);
      } catch (error) {
        // A 404 here means the watchlist was deleted on another tab or device.
        // Refreshing the list lets the selection fall back to a live one now,
        // rather than polling a dead id until the lists happen to go stale.
        if ((error as AxiosError)?.response?.status === 404) {
          queryClient.invalidateQueries({ queryKey: ["watchlists"] });
        }
        throw error;
      }
    },
    refetchOnWindowFocus: false, // Don't refetch on focus
    refetchInterval: isTourMode ? false : 10 * 1000, // Poll every 10 seconds for live prices
    staleTime: isTourMode ? Infinity : 0, // Never stale in tour mode
    gcTime: 10 * 60 * 1000, // 10 minutes - keep in cache longer
    // A deleted watchlist won't come back by retrying the same dead id.
    retry: (failureCount, error) =>
      (error as unknown as AxiosError)?.response?.status === 404 ? false : failureCount < 3,
    // Keep showing previous data during refetch to prevent $0.00 flash on back navigation
    placeholderData: (previousData) => previousData,
  });
};
